import SimpleDocUploader from "lib/components/common/docs/simple-doc-uploader";
import FlexBox from "lib/components/common/flex-box";
import SimpleButton from "lib/components/common/simple-button";
import SimpleCard from "lib/components/common/simple-card";
import SimpleModal from "lib/components/common/simple-modal";
import SimpleTree from "lib/components/common/tree/simple-tree";
import TreeUtils from "lib/utils/tree-utils";
import { openUrlInNewTab } from "lib/utils/util";
import { FC, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { PiPlusBold } from "react-icons/pi";
import { toast } from "sonner";

interface PractitionerDocumentsCardProps {
  isLoading?: boolean;
  isEditable?: boolean;
  error?: string;
  documents?: PractitionerDocuments;
  onDocumentsUpdate?: (
    profilePic?: File,
    educationalCertificate?: File,
    qualificationDoc?: File,
    insuranceDoc?: File
  ) => void;
  //onCertificateUpdate?: (value: File) => void;
}

const PractitionerDocumentsCard: FC<PractitionerDocumentsCardProps> = ({
  isLoading,
  isEditable,
  documents,
  error,
  //onCertificateUpdate,
  onDocumentsUpdate,
}) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [userProfilePic, setUserProfilePic] = useState<DMSDocument>(null);
  const [userEducationalCertificate, setUserEducationalCertificate] =
    useState<DMSDocument>(null);
  const [userQualificationDoc, setUserQualificationDoc] = useState<DMSDocument>(null);
  const [userInsuranceDoc, setUserInsuranceDoc] = useState<DMSDocument>(null);

  useEffect(() => {
    if (documents) {
      setUserProfilePic({
        documentUrl: documents?.profileImageUrl,
      });
      setUserEducationalCertificate({
        documentUrl: documents?.educationalCertificateUrl,
      });
      setUserQualificationDoc({
        documentUrl: documents?.qualificationDocUrl,
      });
      setUserInsuranceDoc({
        documentUrl: documents?.insuranceDocUrl,
      });
    }
  }, [documents]);

  const handleOpenModal = () => {
    setUserProfilePic({
      documentUrl: documents?.profileImageUrl,
    });
    setUserEducationalCertificate({
      documentUrl: documents?.educationalCertificateUrl,
    });
    setUserQualificationDoc({
      documentUrl: documents?.qualificationDocUrl,
    });
    setUserInsuranceDoc({
      documentUrl: documents?.insuranceDocUrl,
    });
    setOpenModal(true);
  };

  if (!documents) {
    return (
      <SimpleCard isTitleNoBg title="Documents">
        <p>No documents available</p>
      </SimpleCard>
    );
  }

  const handleUploadClick = () => {
    if (
      userProfilePic?.document ||
      userEducationalCertificate?.document ||
      userQualificationDoc?.document ||
      userInsuranceDoc?.document
    ) {
      onDocumentsUpdate?.(
        userProfilePic?.document,
        userEducationalCertificate?.document,
        userQualificationDoc?.document,
        userInsuranceDoc?.document
      );
      setOpenModal(false);
    } else {
      toast.error("Please select at least one document to upload.");
    }
  };

  return (
    <SimpleCard
      isTitleNoBg
      title="Documents"
      action={
        <>
          {isEditable && (
            <SimpleButton onClick={handleOpenModal}>
              <FlexBox>
                <>Add</> <PiPlusBold />
              </FlexBox>
            </SimpleButton>
          )}
        </>
      }
    >
      <SimpleTree
        isLoading={isLoading}
        className="mt-3"
        nodes={documents?.documents ?? []}
        onNodeClick={(documentId) => {
          const document = TreeUtils.finNodeInTree(
            documents?.documents ?? [],
            documentId
          );

          if (document?.url) {
            openUrlInNewTab(document?.url);
          }
        }}
      />
      <SimpleModal
        loading={isLoading}
        error={error}
        size="lg"
        open={openModal}
        close={() => setOpenModal(false)}
        title="Upload Document"
        footer={
          <FlexBox justifyContent="end">
            <SimpleButton onClick={handleUploadClick}>Upload</SimpleButton>
          </FlexBox>
        }
      >
        <Row>
          <Col sm="4">
            <SimpleCard title="Profile Picture" isTitleNoBg>
              <SimpleDocUploader
                className="mt-3"
                docUrl={userProfilePic?.documentUrl}
                doc={userProfilePic?.document}
                onChange={(files) => setUserProfilePic({ document: files[0] })}
                onDelete={() => setUserProfilePic({ document: null })}
              />
            </SimpleCard>
          </Col>
          <Col sm="4">
            <SimpleCard title="Educational Docs" isTitleNoBg>
              <SimpleDocUploader
                className="mt-3"
                type="document"
                docUrl={userEducationalCertificate?.documentUrl}
                doc={userEducationalCertificate?.document}
                onChange={(files) => setUserEducationalCertificate({ document: files[0] })}
                onDelete={() => setUserEducationalCertificate({ document: null })}
              />
            </SimpleCard>
          </Col>
          <Col sm="4">
            <SimpleCard title="Qualification Docs" isTitleNoBg>
              <SimpleDocUploader
                className="mt-3"
                type="document"
                docUrl={userQualificationDoc?.documentUrl}
                doc={userQualificationDoc?.document}
                onChange={(files) => setUserQualificationDoc({ document: files[0] })}
                onDelete={() => setUserQualificationDoc({ document: null })}
              />
            </SimpleCard>
          </Col>
          <Col sm="4" className="mt-3">
            <SimpleCard title="Insurance Docs" isTitleNoBg>
              <SimpleDocUploader
                className="mt-3"
                type="document"
                docUrl={userInsuranceDoc?.documentUrl}
                doc={userInsuranceDoc?.document}
                onChange={(files) => setUserInsuranceDoc({ document: files[0] })}
                onDelete={() => setUserInsuranceDoc({ document: null })}
              />
            </SimpleCard>
          </Col>
        </Row>
      </SimpleModal>
    </SimpleCard>
  );
};
export default PractitionerDocumentsCard;
